import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagicWand, Hash, Image as ImageIcon, TrendUp, ArrowRight, FolderOpen, Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const quickTools = [
  { to: "/app/script", icon: MagicWand, title: "Script Studio", desc: "AI scripts with viral score", color: "#FF3B6B" },
  { to: "/app/caption", icon: Hash, title: "Caption Engine", desc: "Captions, hashtags, SEO", color: "#00E5FF" },
  { to: "/app/thumbnail", icon: ImageIcon, title: "Thumbnail AI", desc: "Bold viral thumbnails", color: "#EAB308" },
  { to: "/app/trends", icon: TrendUp, title: "Trend Radar", desc: "Live trending topics", color: "#FF3B6B" },
];

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-home">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// COMMAND CENTER</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Welcome back, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-white/50 mt-1">Pick a tool. Forge content. Go viral.</p>
        </div>
        <Link to="/app/script" className="btn-primary px-5 py-2.5 rounded-lg font-medium flex items-center gap-2" data-testid="quick-generate-btn">
          <Sparkle weight="fill" size={16} /> Generate now
        </Link>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { label: "Projects", value: stats?.projects ?? 0 },
          { label: "Scripts", value: stats?.scripts ?? 0 },
          { label: "Captions", value: stats?.captions ?? 0 },
          { label: "Avg viral score", value: stats?.avg_viral_score ?? 0, suffix: "/100", accent: true },
        ].map((s, i) => (
          <div key={i} className="glass rounded-xl p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
            <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            <p className={`mt-2 font-mono text-3xl ${s.accent ? "text-[#FF3B6B]" : ""}`}>
              {s.value}{s.suffix || ""}
            </p>
          </div>
        ))}
      </div>

      {/* QUICK TOOLS */}
      <h2 className="text-lg font-semibold mb-4 text-white/80">Quick tools</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {quickTools.map((t) => (
          <Link to={t.to} key={t.to} className="glass rounded-xl p-5 hover:-translate-y-1 hover:border-white/30 transition-all group" data-testid={`tool-${t.title.toLowerCase().replace(/\s/g, "-")}`}>
            <t.icon weight="duotone" size={28} style={{ color: t.color }} />
            <h3 className="mt-4 font-semibold">{t.title}</h3>
            <p className="text-sm text-white/50 mt-1">{t.desc}</p>
            <ArrowRight size={16} className="mt-3 text-white/30 group-hover:text-[#FF3B6B] group-hover:translate-x-1 transition-all" />
          </Link>
        ))}
      </div>

      {/* CTA STRIP */}
      <div className="glass-strong rounded-2xl p-8 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[300px] h-[300px] bg-[#FF3B6B]/15 blur-[100px] rounded-full pointer-events-none" />
        <div className="relative flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-2xl font-bold tracking-tighter">Open the Workspace</h3>
            <p className="text-white/60 text-sm mt-1">Organize projects, save drafts, schedule with the content calendar.</p>
          </div>
          <Link to="/app/workspace" className="btn-primary px-5 py-2.5 rounded-lg font-medium flex items-center gap-2" data-testid="open-workspace-btn">
            <FolderOpen size={16} /> Open Workspace
          </Link>
        </div>
      </div>
    </div>
  );
}
